import { FILTERS } from "../components/FilterSection/data/filtersList";
import { FilterState, defaultFiltersState } from "./formatFilterState";

export type FilterNameType = (typeof FILTERS)[number]["name"] | "Filters" | "Sort" | "Housing Fest";

export const getBudgetAppliedStatus = (filterState: FilterState) =>
  !(
    JSON.stringify(filterState?.budget) ===
      JSON.stringify(defaultFiltersState?.range_available_price) ||
    Boolean(filterState?.budget) === false
  );

export const getMoveInMonthAppliedStatus = (filterState: FilterState) =>
  !(
    filterState?.moveInMonth === defaultFiltersState.move_in_month ||
    Boolean(filterState?.moveInMonth) === false
  );

export const getStayDurationAppliedStatus = (filterState: FilterState) =>
  !!(
    (filterState?.minLeaseDuration && filterState?.minLeaseDuration !== "all_selected") ||
    (filterState?.maxLeaseDuration && filterState?.maxLeaseDuration !== "")
  );

export const getStayDurationButtonActiveStatus = (filterState: FilterState) =>
  !!(filterState?.minLeaseDuration || filterState?.maxLeaseDuration);

export const getRoomTypeAppliedStatus = (filterState: FilterState, isPropertyFilter = false) =>
  isPropertyFilter
    ? !(Boolean(filterState?.roomType?.length) === false)
    : !(Boolean(filterState?.roomType?.length) === false);

export const getAvailabilityStatus = (filterState: FilterState) =>
  !(Boolean(filterState?.available && filterState?.available !== "false") === false);

export const getDualOccupancyStatus = (filterState: FilterState, isPropertyFilter = false) =>
  isPropertyFilter
    ? !(Boolean(filterState?.dualOccupancy?.length) === false)
    : !(Boolean(filterState?.dualOccupancy) === false);

export const getAccommodationTypeStatus = (filterState: FilterState) =>
  !(Boolean(filterState?.accommodationType && filterState?.accommodationType?.length) === false);

export const getSortAppliedStatus = (filterState: FilterState) => {
  if (
    filterState?.sortKey &&
    filterState.sortKey !== "relevance_sorting_v2,desc" &&
    filterState.sortKey !== ","
  ) {
    return true;
  }
  if (filterState?.sortKey === "") {
    return true;
  }
  return false;
};

export const getGuarantorStatus = (filterState: FilterState) =>
  Boolean(filterState?.guarantor && filterState?.guarantor !== "");

export const getUniversityAppliedStatus = (filterState: FilterState) =>
  !(Boolean(filterState?.university) === false);

export const getDistanceFromCityCenterStatus = (filterState: FilterState) =>
  !(Boolean(filterState?.region_radius) === false);
export const getLocalityAppliedStatus = (filterState: FilterState) =>
  !(Boolean(filterState?.locality) === false) &&
  !(filterState?.locality && String(filterState.locality).includes("near-by"));

export const getHousingTypeStatus = (filterState: FilterState) =>
  Boolean(filterState?.housingType && filterState?.housingType !== "all");

export const getAmenitiesStatus = (filterState: FilterState) =>
  Boolean(
    filterState?.amenities &&
      (Array.isArray(filterState.amenities) ? filterState.amenities.filter(Boolean) : [])?.length >
        0,
  );

export const getBillsIncludedStatus = (filterState: FilterState) =>
  Boolean(
    filterState?.billsIncluded &&
      (Array.isArray(filterState.billsIncluded) ? filterState.billsIncluded.filter(Boolean) : [])
        ?.length > 0,
  );

export const getCancellationPolicyStatus = (filterState: FilterState) => {
  const value = filterState?.cancellationPolicy;
  const list = Array.isArray(value) ? value : String(value || "").split(",");
  return list.filter(Boolean).length > 0;
};

export const getAmberSaleStatus = (filterState: FilterState) =>
  Boolean(filterState?.amberSaleStatus && filterState?.amberSaleStatus === "true");

export const getFiltersStateActiveStatus = (filterState: FilterState) => {
  const allStatus: boolean = [
    getBudgetAppliedStatus,
    getMoveInMonthAppliedStatus,
    getStayDurationAppliedStatus,
    getRoomTypeAppliedStatus,
    getAvailabilityStatus,
    getDualOccupancyStatus,
    getAccommodationTypeStatus,
    getGuarantorStatus,
    getDistanceFromCityCenterStatus,
    getHousingTypeStatus,
    getAmenitiesStatus,
    getBillsIncludedStatus,
    getAmberSaleStatus,
    getCancellationPolicyStatus,
  ].reduce(
    (acc: boolean, curr: (filterState: FilterState) => boolean) =>
      acc || curr.call(this, filterState),
    false,
  );

  return allStatus;
};

const getFilterActiveStatus = (
  filterName: FilterNameType | "Filters",
  filterState: FilterState,
  isPropertyFilter?: boolean,
  isButtonStatus?: boolean,
) => {
  switch (filterName) {
    case "Budget":
      return getBudgetAppliedStatus(filterState);

    case "Move in Month":
      return getMoveInMonthAppliedStatus(filterState);

    case "Stay Duration":
      return isButtonStatus
        ? getStayDurationButtonActiveStatus(filterState)
        : getStayDurationAppliedStatus(filterState);

    case "Room Type":
      return getRoomTypeAppliedStatus(filterState, isPropertyFilter);

    case "Sort":
      return getSortAppliedStatus(filterState);

    case "University":
      return getUniversityAppliedStatus(filterState);

    case "Locality":
      return getLocalityAppliedStatus(filterState);

    case "Availability":
      return getAvailabilityStatus(filterState);

    case "Dual Occupancy":
      return getDualOccupancyStatus(filterState, isPropertyFilter);

    case "Property Type":
      return getAccommodationTypeStatus(filterState);

    case "Guarantor":
      return getGuarantorStatus(filterState);
    case "Distance from City Centre":
      return getDistanceFromCityCenterStatus(filterState);
    case "Housing Type":
      return getHousingTypeStatus(filterState);
    case "Amenities":
      return getAmenitiesStatus(filterState);
    case "Bills Included":
      return getBillsIncludedStatus(filterState);
    case "Housing Fest":
      return getAmberSaleStatus(filterState);
    case "Cancellation Policy":
      return getCancellationPolicyStatus(filterState);
    case "Filters":
      return getFiltersStateActiveStatus(filterState);

    default:
      return false;
  }
};

export default getFilterActiveStatus;
